// Detalhe de um PI: os números da planilha, o faturamento período a período e
// os pedidos de cliente ainda em aberto que estão amarrados a ele.

import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { apiService } from '@/services/api';
import { useTheme } from '@/contexts/theme-context';
import { obterCoresValor } from './carteira-viz';
import { PiDetalhe, dataCurta, moeda, moedaExata } from './carteira-types';
import { CustoPorPi } from './custo-types';

function Campo({ rotulo, valor }: { rotulo: string; valor: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light uppercase tracking-wide">
        {rotulo}
      </p>
      <p className="text-sm text-schmersal-gray-dark dark:text-white mt-0.5">{valor}</p>
    </div>
  );
}

export function PiDetalheModal({ id, custo, onFechar }: {
  id: string;
  /** Custo acumulado do PI. Vem null para quem não é admin. */
  custo?: CustoPorPi | null;
  onFechar: () => void;
}) {
  const { theme } = useTheme();
  const cores = obterCoresValor(theme);
  const [pi, setPi] = useState<PiDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const res = await apiService.detalharPiCarteira(id);
        if (cancelado) return;
        if (!res.success) { setErro(res.message || 'PI não encontrado'); return; }
        setPi(res.data as PiDetalhe);
      } catch (e) {
        if (!cancelado) setErro(e instanceof Error ? e.message : 'Falha ao carregar o PI');
      }
    })();
    return () => { cancelado = true; };
  }, [id]);

  useEffect(() => {
    const fechar = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', fechar);
    return () => window.removeEventListener('keydown', fechar);
  }, [onFechar]);

  const maiorFaturamento = pi ? Math.max(1, ...pi.faturamentos.map((f) => f.valor)) : 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onFechar}>
      <div
        className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <div className="min-w-0">
            <h2 className="font-semibold text-schmersal-gray-dark dark:text-white">
              {pi?.codigo ?? 'Carregando…'}
            </h2>
            {pi?.descricao && (
              <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-0.5 break-words">
                {pi.descricao}
              </p>
            )}
          </div>
          <button
            onClick={onFechar}
            aria-label="Fechar"
            className="p-1 rounded hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark shrink-0"
          >
            <X className="h-5 w-5 text-schmersal-gray-medium" aria-hidden />
          </button>
        </div>

        {erro && <p className="p-5 text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">{erro}</p>}

        {!pi && !erro && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-schmersal-blue" aria-hidden />
          </div>
        )}

        {pi && (
          <div className="p-5 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Campo rotulo="Carteira" valor={<span className="tabular-nums font-semibold">{moedaExata(pi.saldo)}</span>} />
              <Campo
                rotulo="Faturado"
                valor={<span className="tabular-nums font-semibold">{moedaExata(pi.realizado)}</span>}
              />
              <Campo rotulo="A faturar" valor={<span className="tabular-nums font-semibold">{moedaExata(pi.aFaturar)}</span>} />
              <Campo
                rotulo="Custo p/ finalizar"
                valor={<span className="tabular-nums">{pi.custoParaFinalizar === null ? '—' : moedaExata(pi.custoParaFinalizar)}</span>}
              />
              <Campo
                rotulo="Previsto"
                valor={<span className="tabular-nums">{moedaExata(pi.previsto)}</span>}
              />
              <Campo rotulo="Squad" valor={pi.squad ?? '—'} />
              <Campo rotulo="Situação" valor={pi.situacao ?? '—'} />
              <Campo rotulo="Fam. Coml" valor={pi.famComl ?? '—'} />
              <Campo
                rotulo="Previsão de encerramento"
                valor={pi.previsao ? dataCurta(pi.previsao) : (pi.previsaoTexto ?? '—')}
              />
            </div>

            {custo && (
              <div className="rounded-lg border border-rg-line dark:border-schmersal-gray-dark p-4">
                <h3 className="font-semibold text-schmersal-gray-dark dark:text-white mb-2">Custo do time neste PI</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Campo
                    rotulo="Acumulado"
                    valor={<span className="tabular-nums font-semibold">{moedaExata(custo.custoAcumulado)}</span>}
                  />
                  <Campo rotulo="Meses lançados" valor={<span className="tabular-nums">{custo.meses}</span>} />
                  <Campo
                    rotulo="Custo / carteira"
                    valor={<span className="tabular-nums">{custo.custoSobreSaldo === null ? '—' : `${custo.custoSobreSaldo.toFixed(1)}%`}</span>}
                  />
                  <Campo
                    rotulo="Faturado − custo"
                    valor={<span className="tabular-nums">{moedaExata(pi.realizado - custo.custoAcumulado)}</span>}
                  />
                </div>
                <p className="mt-3 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
                  <span className="font-medium text-schmersal-gray-dark dark:text-white">Quem está no PI:</span>{' '}
                  {custo.nomes.join(', ')}
                </p>
              </div>
            )}

            {(pi.statusObs || pi.acoes) && (
              <div className="rounded-lg bg-schmersal-gray-light/40 dark:bg-schmersal-gray-dark/40 p-4 space-y-2">
                {pi.statusObs && <Campo rotulo="Status / observação" valor={pi.statusObs} />}
                {pi.acoes && <Campo rotulo="Ações" valor={pi.acoes} />}
              </div>
            )}

            <div>
              <h3 className="font-semibold text-schmersal-gray-dark dark:text-white mb-1">
                Faturamento por período
              </h3>
              <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mb-3">
                Mês fechado é faturamento; o mês em curso e os seguintes são o que a
                planilha programa.
              </p>
              {pi.faturamentos.length === 0 ? (
                <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
                  Nenhum valor lançado na planilha para este PI — nem realizado, nem programado.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {pi.faturamentos.map((f) => (
                    <li key={`${f.ano}-${f.mes ?? 'ano'}`} className="flex items-center gap-3">
                      <span className="w-20 shrink-0 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
                        {f.rotulo}
                      </span>
                      {/* Barra de proporção dentro do próprio PI. Azul = entrou;
                          amarelo = ainda não — mesma convenção do painel. */}
                      <span className="flex-1 h-2 rounded-full bg-schmersal-gray-light dark:bg-schmersal-gray-dark overflow-hidden">
                        <span
                          className="block h-full rounded-full"
                          style={{
                            width: `${Math.max(2, (f.valor / maiorFaturamento) * 100)}%`,
                            background: f.realizado ? cores.realizado : cores.previsto,
                          }}
                        />
                      </span>
                      <span className="w-32 shrink-0 text-right text-sm tabular-nums text-schmersal-gray-dark dark:text-white">
                        {moeda(f.valor)}
                      </span>
                      {/* O rótulo é o que separa de verdade: a cor é só reforço. */}
                      <span className="w-24 shrink-0 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                        {f.realizado ? 'faturado' : f.emCurso ? 'em curso' : 'previsto'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h3 className="font-semibold text-schmersal-gray-dark dark:text-white mb-3">
                Pedidos em aberto ({pi.pedidos.length})
              </h3>
              {pi.pedidos.length === 0 ? (
                <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
                  Nenhum pedido de cliente em aberto vinculado a este PI.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
                        <th className="py-2 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Cliente</th>
                        <th className="py-2 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Pedido</th>
                        <th className="py-2 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Tipo</th>
                        <th className="py-2 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Região</th>
                        <th className="py-2 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right">Pedido</th>
                        <th className="py-2 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right">Em aberto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pi.pedidos.map((p) => (
                        <tr key={p.id} className="border-b border-schmersal-gray-light/50 dark:border-schmersal-gray-dark/50">
                          <td className="py-2 text-schmersal-gray-dark dark:text-white">{p.cliente ?? '—'}</td>
                          <td className="py-2 text-schmersal-gray-dark dark:text-schmersal-gray-light">{p.pedidoCliente ?? '—'}</td>
                          <td className="py-2 text-schmersal-gray-dark dark:text-schmersal-gray-light">{p.tipo}</td>
                          <td className="py-2 text-schmersal-gray-dark dark:text-schmersal-gray-light">{p.regiao ?? '—'}</td>
                          <td className="py-2 text-right tabular-nums text-schmersal-gray-dark dark:text-schmersal-gray-light">{moeda(p.valorPedido)}</td>
                          <td className="py-2 text-right tabular-nums font-medium text-schmersal-gray-dark dark:text-white">{moeda(p.valorAberto)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
